import { SitesService } from '@services/sites.service';
import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Schedule } from '@classes/holiday/schedule';
import { FormEditBaseComponent } from '@components/form-edit-base.component';
import { HolidayGroupsService } from '@services/holiday-groups.service';
import { SchedulesService } from '@services/schedules.service';
import { UiService } from '@services/ui.service';
import { Site } from '@classes/site';
import { HolidayGroup } from '@classes/holiday/holiday-group';
import { Day } from '@enums/days.enum';

@Component({
  selector: 'app-schedule-edit',
  templateUrl: './schedule-edit.component.html',
  styleUrls: ['./schedule-edit.component.scss']
})
export class ScheduleEditComponent extends FormEditBaseComponent implements OnInit {
  @Input() schedule: Schedule;

  sites: Site[];
  holidayGroups: HolidayGroup[];
  schedules: Schedule[];

  canChooseNoDays = true;

  daysPreselected = [];

  days = [
    {
      value: 1,
      checked: false,
      disabled: false
    },
    {
      value: 2,
      checked: false,
      disabled: false
    },
    {
      value: 3,
      checked: false,
      disabled: false
    },
    {
      value: 4,
      checked: false,
      disabled: false
    },
    {
      value: 5,
      checked: false,
      disabled: false
    },
    {
      value: 6,
      checked: false,
      disabled: false
    },
    {
      value: 0,
      checked: false,
      disabled: false
    }
  ];

  daysSelected = [];

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private schedulesSrv: SchedulesService,
    private holidayGroupsSrv: HolidayGroupsService,
    private sitesSrv: SitesService
  ) {
    super(uiSrv, fb);
  }

  ngOnInit() {
    super.ngOnInit();
    this.initSub();
  }

  initSub(): void {
    const sitesPromise = new Promise((resolve, reject) => {
      this.sitesSrv.getList()
        .subscribe(sitesFromSrv => {
          this.sites = sitesFromSrv;
          resolve(true);
        });
    });

    const holidayGroupsPromise = new Promise((resolve, reject) => {
      this.holidayGroupsSrv.get()
        .subscribe(holidayGroupsFromSrv => {
          this.holidayGroups = holidayGroupsFromSrv;
          resolve(true);
        });
    });

    Promise.all([sitesPromise, holidayGroupsPromise])
      .then(() => {
        this.isLoading = false;
        this.initForm();
      });
  }

  onSubmit(): void {
    this.isLoading = true;
    if (this.schedule) {
      this.update();
    } else {
      this.create();
    }
  }

  onCancel(): void {
    this.editFinished.emit();
  }

  endForm(response: boolean) {
    if (response) {
      this.onSubmit();
    } else {
      this.onCancel();
    }
  }

  private update(): void {
    const holidayCopy: Schedule = JSON.parse(JSON.stringify(this.schedule));
    holidayCopy.site = this.editForm.value.site;
    holidayCopy.startDayTime = this.editForm.value.startDayTime;
    holidayCopy.endDayTime = this.editForm.value.endDayTime;
    holidayCopy.days = this.daysSelected;
    holidayCopy.holidayGroup = this.editForm.value.holidayGroup;

    this.schedulesSrv.put(holidayCopy).subscribe(() => this.isLoading = false);
  }

  private create(): void {
    let startDayTime = this.editForm.value.startDayTime;
    let endDayTime = this.editForm.value.endDayTime;

    this.schedule = new Schedule(
      this.editForm.value.site,
      startDayTime,
      endDayTime,
      this.daysSelected,
      this.editForm.value.holidayGroup
    );

    this.schedulesSrv.post(this.schedule)
      .subscribe(
        () => this.isLoading = false,
        () => this.schedule = null
      );
  }

  private initForm(): void {
    let site = null;
    let startDayTime = null;
    let endDayTime = null;
    let holidayGroup = null;

    if (this.schedule) {
      site = this.schedule.site;
      startDayTime = this.schedule.startDayTime;
      endDayTime = this.schedule.endDayTime;
      holidayGroup = this.schedule.holidayGroup;
      this.schedule.days.map(oneDay => {
        this.days.map(day => {
          if (oneDay === day.value) {
            day.checked = true;
            this.daysPreselected.push(day);
            this.daysSelected.push(day.value);
          }
        })
      })

      this.schedulesSrv.getBySiteId(site.id).subscribe(schedulesFromSrv => {
        this.schedules = schedulesFromSrv.filter(s => s.id !== site.id);
        this.checkDays();
      })
    }

    this.editForm = this.fb.group({
      site: [site, Validators.required],
      startDayTime: [startDayTime, Validators.required],
      endDayTime: [endDayTime, Validators.required],
      holidayGroup: [holidayGroup]
    });

    this.editForm.get('site').valueChanges.subscribe(site => {
      if (site && typeof site !== 'string') {
        this.schedulesSrv.getBySiteId(site.id).subscribe(schedulesFromSrv => {
          this.schedules = schedulesFromSrv;
          this.checkDays();
        })
      }
    })
  }

  selectDay(event, day) {
    if (event.source.checked === true) {
      day.checked = true;
      this.daysSelected.push(day.value);
    } else {
      day.checked = false;
      this.daysSelected.map((oneDay, index) => {
        if (oneDay === day.value) {
          this.daysSelected.splice(index, 1);
        }
      });
    }
  }

  showDayText(day: Day): string {
    return 'billing.schedules.days.' + Day[day];
  }

  checkDays() {
    // reinit
    this.canChooseNoDays = true;
    this.days.map(oneDay => {
      oneDay.disabled = false;
    })

    this.schedules.map(schedule => {
      if (!schedule.days) {
        this.canChooseNoDays = false;
      }
      schedule.days?.map(day => {
        this.days.map(oneDay => {
          if (day === oneDay.value) {
            oneDay.disabled = true;
          }
          if (this.schedule) {
            this.schedule.days.map(d => {
              if (d === oneDay.value) {
                oneDay.disabled = false;
              }
            })
          }
        })
      })
    })
  }
}
